'use client';

import React from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import MaskedInput from 'react-text-mask';
import TextField from '@mui/material/TextField';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import Captcha from '@/components/Captcha';
import CusButton from '@/components/CusButton';
import setAlertValue from '@/components/Alert';
import SubmitButton from '@/components/SubmitButton';
import SvgWechat from '@/components/icons/SvgWechat';
import {
  PRIVACY_ROUTER_PATH,
  TERMS_ROUTER_PATH,
} from '@/constants/router-path';
import { ERROR_TRANSLATE } from '@/constants/auth';
import { TRACK_NAME } from '@/constants/site-track';
import { baseLogin } from './baseLogin';
import { LOGIN_CODE_CAPTCHA_CLICK_ID } from '@/constants/component-id';
import validators from '@/utils/validators';
import { sendEvent } from '@/utils/site-track';
import { getLocalStorage, setCookie, setLocalStorage } from '@/utils/storage';
import { otpLogin, sendOtp } from '@/api/auth';

const TEL_MASK_ARRAY = [
  /1/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

const OPT_MASK_ARRAY = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/];

const defaultError = {
  tel: '',
  otp: '',
  checked: '',
};

const DEFAULT_COUNT_DOWN = 60;

const TelInput = React.forwardRef<HTMLElement, InputBaseComponentProps>(
  (props, ref) => {
    const setRef = React.useCallback(
      (maskedInputRef: { inputElement: HTMLElement } | null) => {
        const value = maskedInputRef ? maskedInputRef.inputElement : null;

        if (typeof ref === 'function') {
          ref(value);
        } else if (ref) {
          ref.current = value;
        }
      },
      [ref],
    );
    return (
      <MaskedInput
        {...props}
        mask={TEL_MASK_ARRAY}
        guide={false}
        ref={setRef}
      />
    );
  },
);

const OptInput = React.forwardRef<HTMLElement, InputBaseComponentProps>(
  (props, ref) => {
    const setRef = React.useCallback(
      (maskedInputRef: { inputElement: HTMLElement } | null) => {
        const value = maskedInputRef ? maskedInputRef.inputElement : null;

        if (typeof ref === 'function') {
          ref(value);
        } else if (ref) {
          ref.current = value;
        }
      },
      [ref],
    );
    return (
      <MaskedInput
        {...props}
        mask={OPT_MASK_ARRAY}
        guide={false}
        ref={setRef}
      />
    );
  },
);

interface Props {
  headerTitle: string;
  headerTitleClassName?: string;
  subTitle?: string;
  textFieldSx?: React.CSSProperties;
  setIsCodeLogin?: React.Dispatch<React.SetStateAction<boolean>>;
  defaultSearchParams?: URLSearchParams;
}

const CodeLogin = (props: Props) => {
  const {
    headerTitle,
    headerTitleClassName = 'text-xl',
    setIsCodeLogin,
    subTitle,
    textFieldSx = {
      fontSize: '.875rem',
      lineHeight: '1.375rem',
      background: '#f8fafc',
    },
    defaultSearchParams,
  } = props;

  const [countDown, setCountDown] = React.useState(0);
  const [lastTime, setLastTime] = React.useState('0');
  const [sent, setSent] = React.useState(false);
  const [otp, setOtp] = React.useState('');
  const [allowOtpSend, setAllowOptSend] = React.useState(true);
  const [isLoginIn, setIsLoginIn] = React.useState(false);
  const [errorMessage, setErrorMessage] =
    React.useState<Record<string, string>>(defaultError);
  const [tel, setTel] = React.useState('');
  const searchParams = useSearchParams();
  const telRef = React.useRef<string>('');
  const captchaRef = React.useRef<any>(null);
  const sendDisabled = React.useMemo(
    () => !!validators.telValidator(tel) || !allowOtpSend || countDown > 0,
    [tel, allowOtpSend, countDown],
  );

  const handleSendOtp = React.useCallback((captchaVerifyParam: string) => {
    const mobileNumber = telRef.current.replace(/[_\s]/g, '');
    if (!mobileNumber || validators.telValidator(mobileNumber)) {
      setAlertValue({
        msg: '请输入正确的手机号',
        severity: 'warning',
      });
      captchaRef.current?.initCaptcha();
      return;
    }
    setAllowOptSend(false);
    const data = {
      mobile_number: mobileNumber,
      captcha_verify_param: captchaVerifyParam,
    };
    sendOtp(data)
      .then(() => {
        const time = `${Math.round(new Date().getTime() / 1000)}`;
        setAllowOptSend(false);
        setSent(true);
        setCountDown(DEFAULT_COUNT_DOWN);
        setLastTime(time);
        setLocalStorage('lastVerificationCodeTime', time);
      })
      .catch((err: any) => {
        captchaRef.current?.initCaptcha();
        setAllowOptSend(true);
        setAlertValue({
          msg:
            ERROR_TRANSLATE[err.response?.data?.detail] ?? '登录失败，请重试',
        });
      });
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const telErr = validators.telValidator(tel);
    if (telErr) {
      setErrorMessage({
        tel: telErr,
      });
      return;
    }
    const otpErr = validators.optValidator(otp);
    if (otpErr) {
      setErrorMessage({
        otp: otpErr,
      });
      return;
    }
    setIsLoginIn(true);
  };

  const handleLogin = async () => {
    const mobileNumber = tel.replace(/[_\s]/g, '');
    otpLogin(mobileNumber, otp)
      .then(async (res) => {
        const status = res.status;
        if (status === 200 || status === 201) {
          const user = res.data;
          if (!user || !user.is_authenticated) {
            setIsLoginIn(false);
            setAlertValue({
              msg: '登录失败，请重试',
            });
            return;
          }
          if (status === 201) {
            await sendEvent({ name: TRACK_NAME.user_registered });
          }
          if (user && user.mobile_number) {
            setCookie('user_id', user.mobile_number, 14);
          }
          setAlertValue({
            msg: '登录成功，跳转中...',
            severity: 'success',
          });
          baseLogin(defaultSearchParams || searchParams).then();
        }
      })
      .catch((error) => {
        setAlertValue({
          msg:
            ERROR_TRANSLATE[error.response?.data?.detail] ?? '登录失败，请重试',
        });
        setIsLoginIn(false);
      });
  };

  React.useEffect(() => {
    telRef.current = tel;
  }, [tel]);

  React.useEffect(() => {
    const countdownIntervalID = setInterval(() => {
      setCountDown(Math.round(60 - (new Date().getTime() / 1000 - +lastTime)));
    }, 500);
    if (countDown <= 0) {
      setAllowOptSend(true);
      clearInterval(countdownIntervalID);
      captchaRef.current?.initCaptcha();
    }
    return () => clearInterval(countdownIntervalID);
  }, [countDown, lastTime]);

  React.useEffect(() => {
    const time = getLocalStorage('lastVerificationCodeTime') ?? '0';
    setLastTime(time);
    const countDown = Math.round(
      DEFAULT_COUNT_DOWN - (new Date().getTime() / 1000 - +time),
    );
    if (countDown > 0) {
      setCountDown(countDown);
    }
  }, []);

  return (
    <>
      <h1 className={`font-semibold text-center ${headerTitleClassName}`}>
        {headerTitle}
      </h1>
      {subTitle && (
        <p className="mt-3 pb-4 text-base text-center text-web-grey-600">
          {subTitle}
        </p>
      )}
      <form
        className="text-xl leading-5.5 mt-8 tablet:text-sm"
        onSubmit={handleSubmit}
        onChange={() => setErrorMessage(defaultError)}
      >
        <TextField
          error={!!errorMessage.tel}
          helperText={errorMessage.tel}
          variant="outlined"
          type="tel"
          placeholder="请输入手机号"
          fullWidth={true}
          onChange={(e) => setTel(e.target.value)}
          InputProps={{
            inputComponent: TelInput,
            sx: {
              color: errorMessage.tel ? '#b91c1c' : '#000',
              fieldset: {
                border: 'none',
                padding: 0,
              },
              ...textFieldSx,
            },
            inputProps: {
              value: tel,
            },
          }}
          sx={{
            '.Mui-error': {
              margin: '8px 0 0',
            },
          }}
        />
        <div className="relative mt-8">
          <TextField
            error={!!errorMessage.otp}
            helperText={errorMessage.otp}
            variant="outlined"
            type="text"
            placeholder="请输入验证码"
            fullWidth={true}
            onChange={(e) => setOtp(e.target.value)}
            InputProps={{
              inputComponent: OptInput,
              endAdornment: (
                <CusButton
                  className={`w-28 shrink-0 text-right text-sm leading-5.5 ${
                    sendDisabled ? 'text-gray-400 cursor-default' : 'text-denim'
                  }`}
                  type="button"
                  disabled={sendDisabled}
                  id={LOGIN_CODE_CAPTCHA_CLICK_ID}
                >
                  {countDown <= 0
                    ? sent
                      ? '重新发送'
                      : '获取验证码'
                    : `重新发送 (${countDown})`}
                </CusButton>
              ),
              sx: {
                color: errorMessage.otp ? '#b91c1c' : '#000',
                fieldset: {
                  border: 'none',
                  padding: 0,
                },
                ...textFieldSx,
              },
            }}
            sx={{
              '.Mui-error': {
                margin: '8px 0 0',
              },
            }}
          />
          <Captcha onSuccess={handleSendOtp} ref={captchaRef} />
        </div>
        <SubmitButton
          submit={isLoginIn}
          submitText="登录/注册"
          className="!h-11 !mt-8"
          handleSubmit={handleLogin}
        />
      </form>
      <div className="flex justify-center my-6 tablet:my-8">
        <div
          className="flex items-center justify-center text-sm cursor-pointer hover:text-web-blue-500"
          onClick={() =>
            setIsCodeLogin
              ? setIsCodeLogin(false)
              : setAlertValue({ msg: '暂不支持该方式登录' })
          }
        >
          <SvgWechat />
          <span className="ml-2">微信登录/注册</span>
        </div>
      </div>
      <p className="text-xs leading-5 text-black/65 text-center">
        已阅读并同意
        <Link href={TERMS_ROUTER_PATH} className="text-web-blue-500">
          《用户协议》
        </Link>
        和
        <Link href={PRIVACY_ROUTER_PATH} className="text-web-blue-500">
          《隐私政策》
        </Link>
      </p>
    </>
  );
};

export default CodeLogin;
